import type SafeAppsSDK from "@safe-global/safe-apps-sdk";
import type { SafeInfo } from "@safe-global/safe-apps-sdk";

import type { EthersProvider, SafeConnection } from "@/safe/SafeContextValue";
import {
  resolveRuntimeState,
  type RuntimeAuthenticityState,
  type RuntimeConfigPosture,
  type RuntimeDowngradePolicy,
  type RuntimeSafeParentOrigin
} from "@/safe/runtimePolicy";

type SafeRuntimeInputs = {
  authenticity: RuntimeAuthenticityState;
  config: RuntimeConfigPosture;
  parentOrigin: RuntimeSafeParentOrigin;
  downgradePolicy: RuntimeDowngradePolicy;
};

export function resolveInitialSafeConnection(params: SafeRuntimeInputs): SafeConnection {
  return {
    mode: "OUTSIDE_SAFE",
    connected: false,
    providerStatus: "unavailable",
    runtime: resolveRuntimeState({
      mode: "OUTSIDE_SAFE",
      initializing: true,
      authenticity: params.authenticity,
      config: params.config,
      parentOrigin: params.parentOrigin,
      downgradePolicy: params.downgradePolicy
    })
  };
}

export function resolveOutsideSafeConnection(
  params: SafeRuntimeInputs & {
    provider?: EthersProvider;
    providerStatus: SafeConnection["providerStatus"];
  }
): SafeConnection {
  const connection: SafeConnection = {
    mode: "OUTSIDE_SAFE",
    connected: false,
    providerStatus: params.providerStatus,
    runtime: resolveRuntimeState({
      mode: "OUTSIDE_SAFE",
      authenticity: params.authenticity,
      config: params.config,
      parentOrigin: params.parentOrigin,
      downgradePolicy: params.downgradePolicy
    })
  };

  if (params.provider) connection.provider = params.provider;
  return connection;
}

export function resolveInSafeConnection(
  params: SafeRuntimeInputs & {
    safe: SafeInfo;
    sdk: SafeAppsSDK;
    provider: EthersProvider;
  }
): SafeConnection {
  const runtime = resolveRuntimeState({
    mode: "IN_SAFE",
    safe: params.safe,
    authenticity: params.authenticity,
    config: params.config,
    parentOrigin: params.parentOrigin,
    downgradePolicy: params.downgradePolicy
  });
  const connection: SafeConnection = {
    mode: "IN_SAFE",
    connected: true,
    safe: params.safe,
    provider: params.provider,
    providerStatus: "ready",
    runtime
  };

  if (runtime.capabilities.writePathEnabled) connection.sdk = params.sdk;
  return connection;
}
